/**
 * Checkout Page
 * Fluxo: 1. Plano → 2. Dados (PF/PJ) → 3. Termos → 4. Pagamento PIX → 5. Acesso
 */

import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@payflow/ui";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";

type Plan = "essencial" | "profissional";

const PLAN_INFO: Record<Plan, { label: string; price: number }> = {
  essencial: { label: "Essencial", price: 199 },
  profissional: { label: "Profissional", price: 499 },
};

function parsePlan(raw: string | null): Plan {
  // Aceita novos slugs e os legados (web → essencial, completo → profissional)
  if (raw === "profissional" || raw === "completo") return "profissional";
  return "essencial";
}

type Step = "plan" | "form" | "terms" | "payment" | "success";
type TipoPessoa = "pf" | "pj";

interface FormData {
  tipoPessoa: TipoPessoa;
  nomeEmpresa: string;
  cnpj: string;
  email: string;
  telefone: string;
  nomeResponsavel: string;
  cpfResponsavel: string;
}

interface CheckoutResult {
  signupId: string;
  qrCodePayload: string | null;
  qrCodeImage: string | null;
  expiresAt: string;
}

function maskCpf(v: string): string {
  return v
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

function maskCnpj(v: string): string {
  return v
    .replace(/\D/g, "")
    .slice(0, 14)
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

function maskPhone(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) {
    return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
}

export function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan");

  const [plan, setPlan] = useState<Plan>(parsePlan(planParam));
  const [step, setStep] = useState<Step>(planParam ? "form" : "plan");
  const [form, setForm] = useState<FormData>({
    tipoPessoa: "pj",
    nomeEmpresa: "",
    cnpj: "",
    email: "",
    telefone: "",
    nomeResponsavel: "",
    cpfResponsavel: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkoutResult, setCheckoutResult] = useState<CheckoutResult | null>(null);
  const [, setPaymentStatus] = useState<string>("awaiting_payment");
  const [aceiteTermos, setAceiteTermos] = useState(false);

  useEffect(() => {
    if (step !== "payment" || !checkoutResult?.signupId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/v1/billing/checkout/${checkoutResult.signupId}`);
        const data = await res.json();
        if (data.success && data.data) {
          setPaymentStatus(data.data.status);
          if (data.data.status === "paid") {
            setStep("success");
            clearInterval(interval);
          } else if (data.data.status === "expired") {
            setError("O tempo para pagamento expirou. Tente novamente.");
            clearInterval(interval);
          }
        }
      } catch {
        /* continua polling */
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [step, checkoutResult?.signupId]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload: Record<string, any> = {
        tipoPessoa: form.tipoPessoa,
        email: form.email,
        telefone: form.telefone.replace(/\D/g, ""),
        nomeResponsavel: form.nomeResponsavel,
        cpfResponsavel: form.cpfResponsavel.replace(/\D/g, ""),
        plan,
        aceiteTermos: true,
      };
      if (form.tipoPessoa === "pj") {
        payload.nomeEmpresa = form.nomeEmpresa;
        payload.cnpj = form.cnpj.replace(/\D/g, "");
      }

      const res = await fetch(`${API_BASE}/v1/billing/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error?.message || data.message || "Erro ao processar checkout");
        return;
      }
      setCheckoutResult(data.data);
      setStep("payment");
    } catch {
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [form, plan]);

  const planInfo = PLAN_INFO[plan];

  const isFormValid = (() => {
    if (!form.email || !form.telefone || !form.nomeResponsavel || !form.cpfResponsavel) return false;
    if (form.tipoPessoa === "pj" && (!form.nomeEmpresa || !form.cnpj)) return false;
    return true;
  })();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl"
              style={{
                background: "linear-gradient(135deg, #00E676 0%, #00A844 100%)",
                boxShadow:
                  "0 6px 20px -6px rgba(0, 200, 83, 0.5), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M17 6.5C15.85 4.4 13.6 3 11 3C7.13 3 4 6.13 4 10C4 13.87 7.13 17 11 17C13.6 17 15.85 15.6 17 13.5"
                  stroke="#0A0F1A"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="15.5" cy="10" r="1.4" fill="#0A0F1A" />
              </svg>
            </div>
            <span className="text-xl font-bold text-foreground">
              Cred<span className="text-primary-600">ix</span>
            </span>
          </Link>
          <div className="text-sm text-muted-foreground">Checkout seguro</div>
        </div>
      </header>

      <div className="container mx-auto max-w-2xl px-4 py-12">
        {/* Steps indicator */}
        <div className="mb-8 flex flex-wrap items-center justify-center gap-2 text-xs sm:text-sm">
          <StepBadge active={step === "plan"} done={step !== "plan"}>1. Plano</StepBadge>
          <span className="text-muted-foreground">—</span>
          <StepBadge active={step === "form"} done={["terms", "payment", "success"].includes(step)}>
            2. Dados
          </StepBadge>
          <span className="text-muted-foreground">—</span>
          <StepBadge active={step === "terms"} done={["payment", "success"].includes(step)}>
            3. Termos
          </StepBadge>
          <span className="text-muted-foreground">—</span>
          <StepBadge active={step === "payment"} done={step === "success"}>
            4. Pagamento
          </StepBadge>
          <span className="text-muted-foreground">—</span>
          <StepBadge active={step === "success"} done={false}>
            5. Acesso
          </StepBadge>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Step 1: Plan confirmation */}
        {step === "plan" && (
          <Card>
            <CardHeader>
              <CardTitle>Escolha seu plano</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(["essencial", "profissional"] as Plan[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setPlan(p)}
                  className={`w-full rounded-lg border-2 p-4 text-left transition ${
                    plan === p ? "border-primary-500 bg-primary-50" : "border-border hover:border-primary-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-lg font-bold text-foreground">{PLAN_INFO[p].label}</span>
                      {p === "profissional" && (
                        <span className="ml-2 rounded bg-primary-500 px-2 py-0.5 text-xs text-white">
                          Mais escolhido
                        </span>
                      )}
                    </div>
                    <span className="text-2xl font-bold text-foreground">
                      R$ {PLAN_INFO[p].price}
                      <span className="text-sm font-normal text-muted-foreground">/mês</span>
                    </span>
                  </div>
                </button>
              ))}
              <Button className="mt-4 w-full" size="lg" onClick={() => setStep("form")}>
                Continuar
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Form (PF / PJ) */}
        {step === "form" && (
          <Card>
            <CardHeader>
              <CardTitle>
                Seus dados · Plano {planInfo.label} (R$ {planInfo.price}/mês)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-5">
                {/* Tipo PF/PJ */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-foreground">
                    Tipo de cadastro
                  </label>
                  <div
                    className="grid grid-cols-2 gap-0 rounded-xl border border-border p-1"
                    style={{ background: "rgba(148,163,184,0.06)" }}
                  >
                    {(["pj", "pf"] as TipoPessoa[]).map((tp) => (
                      <button
                        key={tp}
                        type="button"
                        onClick={() =>
                          setForm((f) => ({ ...f, tipoPessoa: tp, cnpj: "", nomeEmpresa: "" }))
                        }
                        className={`rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${
                          form.tipoPessoa === tp
                            ? "bg-primary-500 text-white shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {tp === "pj" ? "Pessoa Jurídica" : "Pessoa Física"}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {form.tipoPessoa === "pj"
                      ? "Entre com dados da empresa (CNPJ) e do responsável."
                      : "Cadastro simplificado com seus dados pessoais (CPF)."}
                  </p>
                </div>

                {/* PJ: Empresa + CNPJ */}
                {form.tipoPessoa === "pj" && (
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-foreground">
                        Nome da empresa
                      </label>
                      <input
                        type="text"
                        value={form.nomeEmpresa}
                        onChange={(e) => setForm({ ...form, nomeEmpresa: e.target.value })}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                        placeholder="Empresa ABC Ltda"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-foreground">CNPJ</label>
                      <input
                        type="text"
                        value={form.cnpj}
                        onChange={(e) => setForm({ ...form, cnpj: maskCnpj(e.target.value) })}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                        placeholder="00.000.000/0000-00"
                      />
                    </div>
                  </div>
                )}

                {/* Contato */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">E-mail</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                      placeholder={form.tipoPessoa === "pj" ? "contato@empresa.com" : "voce@email.com"}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      {form.tipoPessoa === "pj" ? "Telefone" : "Celular / WhatsApp"}
                    </label>
                    <input
                      type="tel"
                      value={form.telefone}
                      onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                </div>

                {form.tipoPessoa === "pj" && <hr className="border-border" />}

                <p className="text-sm font-medium text-muted-foreground">
                  {form.tipoPessoa === "pj" ? "Responsável" : "Seus dados"}
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">
                      Nome completo
                    </label>
                    <input
                      type="text"
                      value={form.nomeResponsavel}
                      onChange={(e) => setForm({ ...form, nomeResponsavel: e.target.value })}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                      placeholder="João Silva"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">CPF</label>
                    <input
                      type="text"
                      value={form.cpfResponsavel}
                      onChange={(e) =>
                        setForm({ ...form, cpfResponsavel: maskCpf(e.target.value) })
                      }
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-foreground"
                      placeholder="000.000.000-00"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setStep("plan")}>
                    Voltar
                  </Button>
                  <Button
                    className="flex-1"
                    size="lg"
                    onClick={() => setStep("terms")}
                    disabled={!isFormValid}
                  >
                    Continuar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Termos + Política de Privacidade */}
        {step === "terms" && (
          <Card>
            <CardHeader>
              <CardTitle>Termos e Política de Privacidade</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-5">
                <p className="text-sm text-muted-foreground">
                  Antes de prosseguir, leia e aceite os termos abaixo. Guardamos a data, hora
                  e IP do aceite para fins de compliance (LGPD).
                </p>

                {/* Scrollable resumo */}
                <div
                  className="max-h-64 overflow-y-auto rounded-xl border border-border bg-muted/30 p-4 text-sm leading-relaxed text-foreground"
                  style={{ scrollbarWidth: "thin" }}
                >
                  <h3 className="mb-2 font-bold">1. Termos de Uso</h3>
                  <p className="mb-3 text-muted-foreground">
                    Ao contratar a Credix você adquire uma licença de uso do software como
                    serviço (SaaS) para gerar, enviar e acompanhar cobranças dos seus próprios
                    clientes. Você é responsável pela veracidade dos dados cadastrados e pela
                    relação comercial com seus clientes finais.
                  </p>
                  <h3 className="mb-2 font-bold">2. Cobrança e cancelamento</h3>
                  <p className="mb-3 text-muted-foreground">
                    O plano é cobrado mensalmente via PIX. Você pode cancelar a qualquer momento
                    — sem fidelidade nem multa. Não há reembolso do mês corrente já pago.
                  </p>
                  <h3 className="mb-2 font-bold">3. Proteção de dados (LGPD)</h3>
                  <p className="mb-3 text-muted-foreground">
                    Tratamos seus dados pessoais e os dos seus clientes conforme a Lei
                    13.709/2018. Aplicamos criptografia em trânsito e em repouso, isolamento
                    por tenant e auditoria de todas as operações. Você é o controlador dos
                    dados dos seus clientes finais; a Credix atua como operadora.
                  </p>
                  <h3 className="mb-2 font-bold">4. Uso aceitável</h3>
                  <p className="mb-3 text-muted-foreground">
                    É proibido usar a plataforma para operações ilícitas, cobrança de dívidas
                    inexistentes, assédio ou qualquer prática que viole o Código de Defesa do
                    Consumidor ou legislação vigente.
                  </p>
                  <h3 className="mb-2 font-bold">5. Suporte e disponibilidade</h3>
                  <p className="text-muted-foreground">
                    SLA de disponibilidade de 99,5%. Suporte por e-mail e WhatsApp em dias
                    úteis. Plano Profissional inclui suporte prioritário.
                  </p>
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-background p-4 transition-colors hover:border-primary-400">
                  <input
                    type="checkbox"
                    checked={aceiteTermos}
                    onChange={(e) => setAceiteTermos(e.target.checked)}
                    className="mt-0.5 h-5 w-5 cursor-pointer rounded border-border text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-foreground">
                    Li e concordo com os <strong>Termos de Uso</strong> e com a{" "}
                    <strong>Política de Privacidade</strong> da Credix.
                  </span>
                </label>

                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setStep("form")}>
                    Voltar
                  </Button>
                  <Button
                    className="flex-1"
                    size="lg"
                    onClick={handleSubmit}
                    disabled={loading || !aceiteTermos}
                  >
                    {loading
                      ? "Gerando PIX..."
                      : `Aceitar e pagar R$ ${planInfo.price} via PIX`}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Payment (QR Code) */}
        {step === "payment" && checkoutResult && (
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Pague via PIX</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="mb-4 text-3xl font-bold text-foreground">R$ {planInfo.price},00</p>

              {checkoutResult.qrCodeImage && (
                <div className="mx-auto mb-4 inline-block rounded-lg bg-white p-4">
                  <img
                    src={`data:image/png;base64,${checkoutResult.qrCodeImage}`}
                    alt="QR Code PIX"
                    className="h-64 w-64"
                  />
                </div>
              )}

              {checkoutResult.qrCodePayload && (
                <div className="mb-6">
                  <p className="mb-2 text-sm text-muted-foreground">Ou copie o código PIX:</p>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={checkoutResult.qrCodePayload}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigator.clipboard.writeText(checkoutResult.qrCodePayload || "")
                      }
                    >
                      Copiar
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <div className="h-3 w-3 animate-pulse rounded-full bg-yellow-500"></div>
                Aguardando pagamento...
              </div>

              <p className="mt-2 text-xs text-muted-foreground">
                O pagamento será detectado automaticamente.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Success */}
        {step === "success" && (
          <Card>
            <CardHeader>
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                <svg
                  className="h-8 w-8 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <CardTitle className="text-center">Pagamento confirmado!</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="mb-6 text-muted-foreground">
                Sua conta foi criada com sucesso. As credenciais de acesso foram enviadas para o e-mail{" "}
                <strong>{form.email}</strong>.
              </p>

              <div className="mb-6 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm">
                <p className="font-medium text-primary-800">
                  Verifique seu e-mail e WhatsApp para as credenciais de acesso.
                </p>
              </div>

              <Link to="/login">
                <Button size="lg" className="w-full">
                  Acessar a Credix
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function StepBadge({
  active,
  done,
  children,
}: {
  active: boolean;
  done: boolean;
  children: React.ReactNode;
}) {
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-medium ${
        active
          ? "bg-primary-500 text-white"
          : done
          ? "bg-primary-100 text-primary-700"
          : "bg-muted text-muted-foreground"
      }`}
    >
      {children}
    </span>
  );
}
